import * as moment from 'moment';
import { Component, DestroyRef, HostBinding, OnInit } from '@angular/core';
import {
  AlertModule,
  DropdownModule,
  FooterModule,
  HeaderModule,
  LinkModule, LoaderConfig,
  LogoModule,
  MainContentModule,
  NavbarModule,
  SidebarModule,
  ThemingService
} from '@michelin/theme';

import { environment } from '../../environments/environment';
import { AuthService, IdentityClaims } from '@michelin/auth';
import { AuthenticationWrapperService } from '../shared/services/authentication-wrapper.service';
import { User } from '../shared/models/user';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { Site } from '../shared/models/site';
import { SiteService } from '../shared/services/site.service';
import { ContextService } from '../shared/services/context.service';
import { UserService } from '../shared/services/user.service';
import { Customer } from '../shared/models/customer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {NavigationConfirmService} from "../shared/services/navigation-confirm.service";

const darkClassName = 'dark-mode';

@Component({
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [
    HeaderModule,
    SidebarModule,
    LinkModule,
    RouterLink,
    LogoModule,
    NavbarModule,
    MatIcon,
    MainContentModule,
    RouterOutlet,
    FooterModule,
    AlertModule,
    TranslatePipe,
    DropdownModule
  ]
})
export class LayoutComponent implements OnInit {
  @HostBinding('class') private className = this.themingService.theme.className;

  year = moment.utc().format('YYYY');
  version = environment.version;
  title = 'tkcFront';
  darkMode = this.className === darkClassName;
  customerCtrl = new FormControl<string | Customer>('');
  siteCtrl = new FormControl<string | Site>('');
  sites: Site[];
  filteredSites: Site[];
  customers: Customer[];
  user: User = null;
  userClaims: Partial<IdentityClaims> = null;
  previousCustomer: string | Customer = null;
  previousSite: string | Site = null;
  isSiteChangedByCustomerChange: boolean = false;
  loaderConfig: LoaderConfig = {
    loading: true,
    type: 'spinner'
  };

  constructor(
    private readonly themingService: ThemingService,
    public readonly authWrapperService: AuthenticationWrapperService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly siteService: SiteService,
    private readonly contextService: ContextService,
    private readonly destroyRef: DestroyRef,
    private readonly authService: AuthService,
    private navigationConfirm: NavigationConfirmService
  ) {}

  /**
   * Toggle theme
   * Light or dark
   */
  setTheme() {
    this.darkMode = !this.darkMode;
    this.className = this.themingService.display(this.darkMode ? darkClassName : '', this.darkMode);
  }

  /**
   * On init
   *
   */
  ngOnInit(): void {
    if (this.authService.hasValidIdToken) {
      this.loaderConfig.loading = true;
      this.userClaims = this.authService.getIdentityClaims();
      this.userService
        .getUserInfos(this.userClaims.preferred_username.toUpperCase())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((userInfos) => {
          this.sites = userInfos.sites;
          this.filteredSites = this.sites;
          this.customers = userInfos.customers;

          //Disabling customer Selection if only one customer
          if (this.customers && this.customers.length == 1) {
            this.customerCtrl.disable();
          }

          //Updating MineId in context when changing
          this.siteCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((site: string | Site) => {
            if (this.isSiteChangedByCustomerChange) {
              this.isSiteChangedByCustomerChange = false;
              this.applySiteChange(site);
            }
            else {
              this.siteCtrl.setValue(this.previousSite, { emitEvent: false });
              this.navigationConfirm.beforeActionWithConfirmation(
                this.contextService.isModificationPending(),
                () => {
                  this.applySiteChange(site)
                },
                undefined
              );
            }
          });

          this.customerCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((customer: string | Customer) => {
            this.customerCtrl.setValue(this.previousCustomer, { emitEvent: false });
            this.navigationConfirm.beforeActionWithConfirmation(
              this.contextService.isModificationPending(),
              () => {
                if (customer && typeof customer === 'object' && customer.id !== undefined) {
                  this.customerCtrl.setValue(customer, { emitEvent: false });
                  this.previousCustomer = customer;
                  this.contextService.setCustomerId(customer.id);
                  this.filteredSites = this.sites.filter((site) => site.customerId === customer.id);
                  if (this.filteredSites && this.filteredSites.length > 0) {
                    //Usage of setTimeout to ensure synchronization with the view and Check the selected value
                    setTimeout(() => {
                      this.isSiteChangedByCustomerChange = true;
                      this.siteCtrl.setValue(this.filteredSites[0]);
                    });
                  }
                } else if (!customer) {
                  this.filteredSites = this.sites;
                }
              },
              undefined
            );
          });

          if (this.customers && this.customers.length > 0) {
            //Usage of setTimeout t ensure synchronization with the view and Check the selected value
            setTimeout(() => {
              this.customerCtrl.setValue(this.customers[0]);
            });
          }
          this.loaderConfig.loading = false;
        });
    }
  }

  /**
   * Apply site change
   * @param site
   * @private
   */
  private applySiteChange(site: string | Site) {
    if (site && typeof site === 'object' && site.id !== undefined) {
      this.previousSite = site;
      setTimeout(() => {
        this.siteCtrl.setValue(site, { emitEvent: false });
      });
      this.contextService.setMineId(site.id);
    }
  }

  /**
   * On logout
   */
  logout() {
    this.authWrapperService.logout(this.router);
  }

  /**
   * On login
   */
  login() {
    this.authWrapperService.onLoginRequired$.next();
  }

  /**
   * Used to marked the selected site in the Select Autocomplete
   * @param a
   * @param b
   */
  compareSites(a: Site, b: Site) {
    return a && b && a.id === b.id;
  }

  /**
   * Used to marked the selected site in the Select Autocomplete
   * @param a
   * @param b
   */
  compareCustomer(a: Customer, b: Customer) {
    return a && b && a.id === b.id;
  }
}
